<template>
    <div class="header-section">
      <div class="content-section">
          <h3 class="section-title">{{ $t('templates.sms_template') }}</h3>
        <div class="flex items-center mb-4 gap-2">
          <button
            @click="openAddTemplateModal"
            class="BTNMAIN py-3 px-6 flex gap-2 items-center"
          >
          <i class="icon-smsir-plus text-[20px]"></i>
            <span class="text-[#0D0D0D] text-base">{{ $t('templates.add_new') }}</span>
          </button>
          <div class="relative filter-dropdown-container">
            <button 
              @click.stop="toggleFilterDropdown($event)" 
              class="flex items-center gap-2 px-6 py-3 bg-[#F2F2F2] rounded-full hover:bg-gray-200"
            >
            <i class="icon-smsir-filter text-[20px]"></i>
              <span class="text-[#0D0D0D] text-base">{{ $t('filter') }}</span>
            </button>
            
            <!-- Filter Dropdown -->
            <div 
              v-if="showFilterDropdown" 
              class="absolute top-full left-0 mt-1 bg-white shadow-lg rounded-lg p-3 z-10 min-w-[200px]"
              @click.stop
            >
              <div v-for="option in statusOptions" :key="option.value" class="flex items-center mb-2">
                <label class="cursor-pointer flex items-center gap-2">
                  <input 
                    type="checkbox" 
                    :value="option.value"
                    :checked="selectedFilters.includes(option.value)"
                    @change="toggleFilter(option.value)"
                    class="mr-2"
                  />
                  <span>
                    {{ option.label === 'all' ? $t('common.all') : $t(`templates.status.${option.label}`) }}
                  </span>
                </label>
              </div>
            </div>
          </div>
          
  
        </div>

        <LoadingSpinner v-if="loading" />
        <div v-else-if="filteredTemplates.length === 0 && templates.length === 0" class="no-templates p-4 text-center bg-gray-50 rounded-lg">
          {{ $t('templates.no_templates') }}
        </div>

        <div v-else-if="filteredTemplates.length === 0" class="no-templates p-4 text-center bg-gray-50 rounded-lg">
          {{ $t('templates.no_matching_templates') }}
        </div>

        <!-- Template Table -->
        <div v-else class="overflow-x-auto">
          <table class="w-full border-collapse">
            <thead>
              <tr class="bg-gray-100">
                <th class="p-3 text-right w-1/4 text-[#4D4D4D] text-sm font-light">{{ $t('templates.title') }}</th>
                <th class="p-3 text-right w-1/4 text-[#4D4D4D] text-sm font-light">{{ $t('templateId') }}</th>
                <th class="p-3 text-right w-1/4 text-[#4D4D4D] text-sm font-light">{{ $t('templates.status.status_label') }}</th>
                <th class="p-3 text-right w-1/4 text-[#4D4D4D] text-sm font-light">{{ $t('templates.actions') }}</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="template in paginatedTemplates" :key="template.id" class="border-b hover:bg-gray-50">
                <td class="p-3 text-base text-[#0D0D0D]">{{ template.title }}</td>
                <td class="p-3 text-base text-[#0D0D0D]">
                  <div class="flex items-center gap-3">
                    <button @click="copyToClipboard(template.id)" class="text-gray-500 hover:text-gray-700">
                      <i class="icon-smsir-copy text-xl text-[#0D0D0D]"></i>
                    </button>
                    <span>{{ template.id }}</span>
                  </div>
                </td>
                <td class="p-3">
                  <span class="template-status" :class="getStatusClass(template.status)">
                    {{ getStatusText(template.status) }}
                  </span>
                </td>
                <td class="p-3">
                  <div class="flex gap-2">
                    <button @click="viewTemplateDetails(template)" class="text-[#0D0D0D]  hover:text-gray-600">
                      <i class="icon-smsir-eye text-2xl"></i>
                    </button>
                    <button @click="editTemplate(template)" class="text-[#0D0D0D]  hover:text-blue-600">
                      <i class="icon-smsir-write text-3xl"></i>
                    </button>
              
                    <button @click="confirmDeleteTemplate(template)" class="text-[#0D0D0D]  hover:text-red-600">
                      <i class="icon-smsir-trash-bin-trash text-2xl"></i>
                    </button>
                  </div>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
        
        <div class="pagination" v-if="shouldShowPagination">
  <button
    @click="goToPage(currentPage - 1)"
    :disabled="currentPage === 1"
    class="px-3 py-1 border rounded"
    :class="{ 'opacity-50 cursor-not-allowed': currentPage === 1 }"
  >
    {{ $t('common.previous') }}
  </button>
  <span class="px-3 py-1">
    {{ currentPage }} / {{ totalPages }}
  </span>
  <button
    @click="goToPage(currentPage + 1)"
    :disabled="currentPage >= totalPages"
    class="px-3 py-1 border rounded"
    :class="{ 'opacity-50 cursor-not-allowed': currentPage >= totalPages }"
  >
    {{ $t('common.next') }}
  </button>
</div>

        <!-- Modal for Add/Edit Template -->
        <div v-if="showModal" class="modal">
          <div class="modal-content">
            <div class="flex justify-between items-center mb-4">
              <h2 class="font-semibold text-[#0D0D0D] text-lg">{{ isEditing ? $t('templates.edit') : $t('templates.add_new') }} " {{ currentTemplate.title }}  "</h2>
              <button @click="closeModal" class="text-2xl">&times;</button>
            </div>
            <form class="mt-4" @submit.prevent="!isSubmitting && saveTemplate()">
              <div class="form-group mb-4">
                <label class="block text-[#4D4D4D] text-sm mb-1">{{ $t('templates.title') }}</label>
                <input 
                  class="input-main w-full"
                  v-model="currentTemplate.title"
                  type="text"
                  required
                  maxlength="32"
                />
              </div>
              <div class="form-group mb-4">
                <label class="block text-[#4D4D4D] text-sm mb-1">{{ $t('templates.template_text') }}</label>
                <textarea
                  class="input-main-textarea w-full"
                  v-model="currentTemplate.templateText"
                  rows="5"
                  required
                ></textarea>
                <div class="mt-2">
                  <div class="text-sm text-gray-700 mb-1 w-full">{{ $t('availableParameters') }}:</div>
                  <div class="flex flex-wrap gap-2">
                    <button 
                      v-for="param in templateParameters" 
                      :key="param.id"
                      @click="insertParameter(param.placeholder)"
                      class="text-xs bg-gray-200 hover:bg-gray-300 rounded px-2 py-1"
                      type="button"
                    >
                      {{ param.label }} ({{ param.placeholder }})
                    </button>
                  </div>
                </div>
                <div class="flex flex-col mt-2">
                  <small class="text-red-600">{{ $t('templates.variable_hint') }}</small>
                  <small class="text-red-600">{{ $t('templates.namad_hint') }}</small>
                </div>
              </div>
              <div class="flex justify-end gap-2 mt-4">
                <button type="button" @click="closeModal" class="px-4 py-2 border border-gray-300 rounded-full hover:bg-gray-100 text-base">
                  {{ $t('common.cancel') }}
                </button>
                <button type="submit" :disabled="isSubmitting" class="BTNMAIN bg-[#12BA6C] flex gap-2 items-center" :class="{ 'opacity-70 cursor-not-allowed': isSubmitting }">
                  <i class="icon-smsir-save text-xl text-white"></i>
                  <span class="text-white text-base">
                    {{ isEditing ? $t('common.update') : $t('common.save') }}
                  </span>
                </button>
              </div>
            </form>
          </div>
        </div>

        <!-- Template Details Modal -->
        <div v-if="showDetailsModal" class="modal">
          <div class="modal-content">
            <div class="flex justify-between items-center mb-4">
              <h2 class="font-semibold text-[#0D0D0D] text-lg">{{ $t('templates.details') }} " {{ currentTemplate.title }}  "</h2>
              <button @click="showDetailsModal = false" class="text-2xl">&times;</button>
            </div>
            <div class="mt-4">
              <div class="mb-2">
                <h3 class="text-sm text-[#4D4D4D]">{{ $t('templateId') }}</h3>
                  <p class="text-[#0D0D0D] text-base mt-3">{{ currentTemplate.id }}</p>
                  <hr class="border-[#D9D9D9] w-full h-[1px] mt-3">
              </div>
              <div class="mb-2">
                <h3 class="text-sm text-[#4D4D4D]">{{ $t('templates.title') }}</h3>
                <p class="text-[#0D0D0D] text-base mt-3">{{ currentTemplate.title }}</p>
                <hr class="border-[#D9D9D9] w-full h-[1px] mt-3">

              </div>
              <div class="mb-2">
                <h3 class="text-sm text-[#4D4D4D]">{{ $t('templates.template_text') }}</h3>
                <p class="text-[#0D0D0D] text-base mt-3 whitespace-pre-wrap break-words">{{ currentTemplate.templateText }}</p>
                <hr class="border-[#D9D9D9] w-full h-[1px] mt-3">

              </div>
              <div class="mb-2">
                <h3 class="text-sm text-[#4D4D4D]">{{ $t('templates.status.status_label') }}</h3>
                <span class="template-status mt-2" :class="getStatusClass(currentTemplate.status)">
                  {{ getStatusText(currentTemplate.status) }}
                </span>
                <hr class="border-[#D9D9D9] w-full h-[1px] mt-3">

              </div>
            
              <div class="flex justify-end mt-4">
                <button @click="showDetailsModal = false" class="px-4 py-2 border border-gray-300 rounded-full hover:bg-gray-100 text-base">
                  {{ $t('common.close') }}
                </button>
              </div>
            </div>
          </div>
        </div>

        <!-- Confirmation Modal -->
        <div v-if="showConfirmModal" class="modal">
          <div class="modal-content max-w-md">
            <div class="flex justify-between items-center mb-4">
              <h2 class="text-[18px] text-[#0D0D0D]">{{ $t('templates.delete_confirm_title') }} " {{ currentTemplate.title }} " {{ $t('templates.delete_confirm_after_title') }}</h2>
              <button @click="showConfirmModal = false" class="text-2xl">&times;</button>
            </div>
            
            <p class="mb-4 text-[#4D4D4D]">{{ $t('templates.delete_confirm_message') }}</p>
            
            <div class="flex justify-end gap-2">
              <button @click="showConfirmModal = false" class="px-4 py-2 border border-gray-300 rounded-full hover:bg-gray-100 text-base">
                {{ $t('common.cancel') }}
              </button>
              <button @click="!isSubmitting && deleteTemplate()" :disabled="isSubmitting" class="px-4 py-2 bg-red-500 text-white rounded-full flex items-center gap-2 text-base" :class="{ 'opacity-70 cursor-not-allowed': isSubmitting }">
                <i class="icon-smsir-trash-bin-trash text-xl text-white"></i>
                {{ $t('common.delete') }}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
</template>

<script>
import axios from 'axios';
import Swal from 'sweetalert2';
import LoadingSpinner from '../LoadingSpinner.vue';

export default {
  name: "TemplateManager",
  components: {
    LoadingSpinner
  },
  props: {
    settings: {
      type: Object,
      required: true,
    },
    active: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      statusOptions: [
        { value: 'all', label: 'all' },
        { value: '2', label: 'verified' },
        { value: '1', label: 'pending' },
        { value: '3', label: 'unverified' }
      ],
      templates: [], 
      filteredTemplates: [],
      paginatedTemplates: [],
      selectedFilters: ['all'],
      showFilterDropdown: false,
      loading: true,
      isSubmitting: false,
      currentPage: 1,
      perPage: 10,
      showModal: false,
      showConfirmModal: false,
      showDetailsModal: false,
      isEditing: false,
      totalItemsCount: 0,
      serverTotalPages: 0,
      currentTemplate: {
        id: null,
        title: '',
        templateText: '',
        status: null
      },
      templateParameters: [

        { id: 1, label: this.$t('orderID'), placeholder: "#OrderId#" },
        { id: 2, label: this.$t('orderTotal'), placeholder: "#OrderTotal#" },
        { id: 3, label: this.$t('customerName'), placeholder: "#CustomerName#" },
        { id: 4, label: this.$t('customerFirstName'), placeholder: "#CustomerFirstName#" },
        { id: 5, label: this.$t('customerLastName'), placeholder: "#CustomerLastName#" },
        { id: 6, label: this.$t('orderDate'), placeholder: "#OrderDate#" },
        { id: 7, label: this.$t('paymentMethod'), placeholder: "#PaymentMethod#" },
        { id: 8, label: this.$t('shippingMethod'), placeholder: "#ShippingMethod#" },
        { id: 9, label: this.$t('orderItems'), placeholder: "#OrderItems#" },
        { id: 10, label: this.$t('orderPrice'), placeholder: "#Price#" },
        { id: 11, label: this.$t('customerPhone'), placeholder: "#CustomerPhone#" },
        { id: 12, label: this.$t('productTitle'), placeholder: "#productTitle#" },
        { id: 13, label: this.$t('productUrl'), placeholder: "#productUrl#" },
        { id: 14, label: this.$t('price'), placeholder: "#Price#" },
        { id: 15, label: this.$t('variation'), placeholder: "#Variation#" },
      ],
    };
  },
  computed: {
    totalPages() {
    // Use server-side pages when available, otherwise calculate client-side
    return this.serverTotalPages || Math.ceil(this.filteredTemplates.length / this.perPage);
  },
  shouldShowPagination() {
    // Show pagination if server says we have multiple pages OR
    // if we have enough items client-side
    return this.serverTotalPages > 1 || 
           (this.filteredTemplates.length > this.perPage && this.serverTotalPages === 0);
  }
  },
  watch: {
    active: {
      immediate: true,
      handler(newValue) {
        if (newValue) {
          this.loadTemplates();
        }
      }
    },
  },
  methods: {
    handleClickOutside(event) {
      const dropdown = this.$el.querySelector('.filter-dropdown-container');
      if (dropdown && !dropdown.contains(event.target) && this.showFilterDropdown) {
        this.showFilterDropdown = false;
      }
    },
    
    toggleFilterDropdown(event) {
      event.stopPropagation();
      this.showFilterDropdown = !this.showFilterDropdown;
    },
    
    copyToClipboard(text) {
      const textarea = document.createElement('textarea');
      textarea.value = text;
      
      // Make the textarea out of viewport
      textarea.style.position = 'fixed';
      textarea.style.left = '-999999px';
      textarea.style.top = '-999999px';
      
      // Append the textarea to the document
      document.body.appendChild(textarea);
      
      // Select the text in the textarea
      textarea.focus();
      textarea.select();
      
      // Execute the copy command
      let successful = false;
      try {
        successful = document.execCommand('copy');
      } catch (err) {
        console.error('Unable to copy to clipboard', err);
      }
      
      // Remove the temporary textarea
      document.body.removeChild(textarea);
      
      // Show a success message using SweetAlert2
      if (successful) {
        Swal.fire({
          text: this.$t('copiedToClipboard'),
          icon: 'success',
          toast: true,
          position: 'top-start',
          showConfirmButton: false,
          timer: 2000,
          timerProgressBar: true,
          customClass: {
            popup: 'smsir-swal'
          }
        });
      } else {
        Swal.fire({
          text: this.$t('failedToCopy'),
          icon: 'error',
          toast: true,
          position: 'top-start',
          showConfirmButton: false,
          timer: 2000,
          timerProgressBar: true,
          customClass: {
            popup: 'smsir-swal'
          }
        });
      }
    },
    
    viewTemplateDetails(template) {
      this.currentTemplate = { ...template };
      this.showDetailsModal = true;
    },
    
    async loadTemplates() {
      this.loading = true;
      try {
        const params = new URLSearchParams({
          action: 'sms_ir_get_templates',
          nonce: window.sms_ir_login.nonce,
          pageNumber: this.currentPage,
          pageSize: this.perPage,
        });

        // Only add status filter if specific statuses are selected
        if (!this.selectedFilters.includes('all')) {
          // Convert status values to a comma-separated string
          const statusFilters = this.selectedFilters.join(',');
          if (statusFilters) {
            params.append('status', statusFilters);
          }
        }

        const response = await axios.post(
          window.sms_ir_login.ajaxUrl,
          params,
          {
            headers: {
              'Content-Type': 'application/x-www-form-urlencoded',
            },
          }
        );

        if (response.data.success) {
          this.templates = response.data.data?.items || [];
          this.totalItemsCount = response.data.data?.totalItemsCount || 0;
          this.serverTotalPages = response.data.data?.totalPagesCount || 1;
          this.filteredTemplates = [...this.templates];
          this.paginatedTemplates = [...this.templates];
        }
      } catch (error) {
        console.error('Error loading templates:', error);
        this.templates = [];
        this.filteredTemplates = [];
        this.paginatedTemplates = [];
      } finally {
        this.loading = false;
      }
    },
    
    paginateTemplates() {
      const start = (this.currentPage - 1) * this.perPage;
      const end = start + this.perPage;
      this.paginatedTemplates = this.filteredTemplates.slice(start, end);
    },
    
    openAddTemplateModal() {
      this.isEditing = false;
      this.currentTemplate = {
        id: null,
        title: '',
        templateText: '',
        status: null
      };
      this.showModal = true;
    },
    
    editTemplate(template) {
      this.isEditing = true;
      this.currentTemplate = { ...template };
      this.showModal = true;
    },
    
    insertParameter(placeholder) {
      // Append the parameter to the template text
      this.currentTemplate.templateText = (this.currentTemplate.templateText || '') + placeholder;
      
      // Focus the textarea after inserting
      this.$nextTick(() => {
        const textarea = document.querySelector('textarea[v-model="currentTemplate.templateText"]');
        if (textarea) {
          textarea.focus();
          textarea.scrollTop = textarea.scrollHeight;
        }
      });
    },
    
    async saveTemplate() {
      // Prevent multiple submissions
      if (this.isSubmitting) {
        return;
      }
      
      try {
        // Set submission state to prevent duplicate requests
        this.isSubmitting = true;
        
        const data = new URLSearchParams({
          action: this.isEditing ? 'sms_ir_update_template' : 'sms_ir_add_template',
          nonce: window.sms_ir_login.nonce,
          title: this.currentTemplate.title,
          template_text: this.currentTemplate.templateText
        });

        if (this.isEditing) {
          data.append('id', this.currentTemplate.id);
        }

        const response = await axios.post(
          window.sms_ir_login.ajaxUrl,
          data,
          {
            headers: {
              'Content-Type': 'application/x-www-form-urlencoded',
            },
          }
        );

        if (response.data.success) {
          Swal.fire({
            text: this.isEditing
              ? this.$t('templates.update_success')
              : this.$t('templates.add_success'),
            icon: 'success',
            toast: true,
            position: 'top-start',
            showConfirmButton: false,
            timer: 3000,
            timerProgressBar: true,
            customClass: {
              popup: 'smsir-swal',
            },
          });
          
          this.closeModal();
          this.loadTemplates();
        } else {
          Swal.fire({
            text: response.data.data,
            icon: 'error',
            toast: true,
            position: 'top-start',
            showConfirmButton: false,
            timer: 3000,
            timerProgressBar: true,
            customClass: {
              popup: 'smsir-swal',
            },
          });
        }
      } catch (error) {
        const errorData = error.response?.data;
        const errorMessage = errorData?.data?.message || 
                           (errorData?.data && this.$t(errorData.data)) || 
                           this.$t('errors.template_save_failed');

        Swal.fire({
          text: errorMessage,
          icon: 'error',
          toast: true,
          position: 'top-start',
          showConfirmButton: false,
          timer: 3000,
          timerProgressBar: true,
          customClass: {
            popup: 'smsir-swal',
          },
        });
      } finally {
        // Reset submission state when request completes (success or error)
        this.isSubmitting = false;
      }
    },
    
    confirmDeleteTemplate(template) {
      this.currentTemplate = template;
      this.showConfirmModal = true;
    },
    
    async deleteTemplate() {
      // Prevent multiple submissions
      if (this.isSubmitting) {
        return;
      }
      
      try {
        this.isSubmitting = true;
        
        const data = new URLSearchParams({
          action: 'sms_ir_delete_template',
          nonce: window.sms_ir_login.nonce,
          id: this.currentTemplate.id
        });

        const response = await axios.post(
          window.sms_ir_login.ajaxUrl,
          data,
          {
            headers: {
              'Content-Type': 'application/x-www-form-urlencoded',
            },
          }
        );

        if (response.data.success) {
          Swal.fire({
            text: this.$t('templates.delete_success'),
            icon: 'success',
            toast: true,
            position: 'top-start',
            showConfirmButton: false,
            timer: 3000,
            timerProgressBar: true,
            customClass: {
              popup: 'smsir-swal',
            },
          });
          
          this.showConfirmModal = false;
          this.loadTemplates();
        } else {
          Swal.fire({
            text: response.data.data || this.$t('errors.template_delete_failed'),
            icon: 'error',
            toast: true,
            position: 'top-start',
            showConfirmButton: false,
            timer: 3000,
            timerProgressBar: true,
            customClass: {
              popup: 'smsir-swal',
            },
          });
        }
      } catch (error) {
        const errorMessage = error.response?.data?.data || 
                           this.$t('errors.template_delete_failed');
        
        Swal.fire({
          text: errorMessage,
          icon: 'error',
          toast: true,
          position: 'top-start',
          showConfirmButton: false,
          timer: 3000,
          timerProgressBar: true,
          customClass: {
            popup: 'smsir-swal',
          },
        });
      } finally {
        this.isSubmitting = false;
      }
    },
    
    closeModal() {
      this.showModal = false;
      this.showConfirmModal = false;
      this.showDetailsModal = false;
      this.isSubmitting = false; // Reset submission state when closing modal
    },
    
    getStatusClass(status) {
      const statusMap = {
        1: 'pending',
        2: 'verified',
        3: 'unverified',
      };
      return statusMap[status] || 'unknown';
    },
    
    getStatusText(status) {
      const statusMap = {
        1: this.$t('templates.status.pending'),
        2: this.$t('templates.status.verified'),
        3: this.$t('templates.status.unverified'),
      };
      return statusMap[status] || this.$t('templates.status.unknown');
    },
    goToPage(page) {
  if (page >= 1 && page <= this.totalPages) {
    this.currentPage = page;
    this.loadTemplates(); // This keeps the filter active
  }
},
   
    toggleFilter(value) {
      if (value === 'all') {
        // If 'all' is selected, clear other filters
        this.selectedFilters = ['all'];
      } else {
        // Remove 'all' from filters
        const index = this.selectedFilters.indexOf('all');
        if (index !== -1) {
          this.selectedFilters = this.selectedFilters.filter(f => f !== 'all');
        }

        // Toggle the selected filter
        const valueIndex = this.selectedFilters.indexOf(value);
        if (valueIndex === -1) {
          // Add the filter
          this.selectedFilters.push(value);
        } else {
          // Remove the filter
          this.selectedFilters.splice(valueIndex, 1);
        }

        // If no filters are selected, select 'all'
        if (this.selectedFilters.length === 0) {
          this.selectedFilters = ['all'];
        }
      }

      // Reset to first page and reload
      this.currentPage = 1;
      this.loadTemplates();
    },
  },
  mounted() {
    this.loadTemplates();
    
    // Add click outside handler for filter dropdown
    document.addEventListener('click', this.handleClickOutside);
  },
  beforeDestroy() {
    // Remove click outside handler
    document.removeEventListener('click', this.handleClickOutside);
  },
};
</script>

<style scoped>
.template-status {
  display: inline-block;
  padding: 8.5px 12px;
  border-radius: 50px;
  font-size: 14px;
  font-weight: 400;
}

.template-status.pending {
  background-color: #FFF9E5;
  border:1px solid #B38600;
  color: #B38600;
}

.template-status.verified {
  background-color: #E5FFF0;
  border:1px solid #0E8B51;
  color: #0E8B51;
}

.template-status.unverified {
  background-color: #FDE9E7;
  border:1px solid #D51D10;
  color: #D51D10;
}

.modal {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1000;
}

.modal-content {
  background: white;
  padding: 24px;
  border-radius: 30px;
  width: 500px;
  max-height: 90vh;
  overflow-y: auto;
}

.pagination {
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 15px;
  margin-top: 20px;
}

/* Add this to close the dropdown when clicking outside */
@media (min-width: 768px) {
  .modal-content {
    width: 600px;
  }
}
</style>
